import { MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import { IItemPreviewState } from "@/components/item-preview";
import { Uuid } from "@/domains/global/identifiers";
import { OnClick } from "@/domains/react/types";
import { ContactObservable } from "@/store/contacts/ContactObservable";
import { ReactNode } from "react";
import { MDSMenuContentList, MDSRowItemAppearance } from "@/mds";

export enum ItemListItemKind {
  Collection = "COLLECTION",
  Note = "NOTE",
  Template = "TEMPLATE",
  Session = "SESSION",
  AudioRecording = "AUDIO_RECORDING",
  Search = "SEARCH",
  Message = "MESSAGE",
  CalendarEvent = "CALENDAR_EVENT",
}

export enum ItemListSharedByVariant {
  SharedBy = "SHARED_BY",
  AddedToCollectionBy = "ADDED_TO_COLLECTION_BY",
}

export type DynamicContent = () => ReactNode;

export interface ExtraRow {
  id: string;
  content: DynamicContent;
}

export interface TitleIcon {
  kind: MDSIconKind;
  color?: MDSIconColor;
  tooltip?: string;
}

export interface ImageInfo {
  mimeType: string;
  url: string;
}

export interface SnippetChunk {
  text: string;
  isHighlight?: boolean;
  isTruncatedStart?: boolean;
  isTruncatedEnd?: boolean;
}

export interface TopicInfo {
  id: string;
  label: string;
}

export enum ItemListCarouselItemVariant {
  Item = "ITEM",
  Collection = "COLLECTION",
  Other = "OTHER",
}

export interface ItemListCarouselItem
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "onClick"> {
  id: string;
  variant: ItemListCarouselItemVariant;
  primaryLabel: string;
  secondaryLabel?: string;
  icon: () => ReactNode;
  onClick: OnClick<{ itemId: string }>;
  isHidden?: boolean;
}

export interface IItemListState {
  isHoverable: boolean;
  isSelectModeActive: boolean;
  canSelect: boolean;
  highlightedItemId?: string;
  highlightItem: (itemId: string) => void;
  isSelected: (itemId: string) => boolean;
  getNextItemId: (itemId: string) => string | undefined;
  selectItemsFromLastSelectionUntil: (itemId: string) => void;
  toggleItemSelection: (itemId: string) => void;
  shouldHideSeparator: (args: { itemId: string; appearance: MDSRowItemAppearance }) => boolean;
}

export type ItemListItem<TKind extends ItemListItemKind = ItemListItemKind> = {
  kind: TKind;
  id: Uuid;
  label: string;
  createPreviewState?: () => IItemPreviewState;
  dateLabel?: string;
  onClick: OnClick<{ itemId: string; event?: React.MouseEvent | MouseEvent }>;
  description?: string;
  titleIcon?: TitleIcon;
  sharedBy?: ContactObservable;
  sharedByVariant?: ItemListSharedByVariant;
  lastEditedDate?: string;
  listState?: IItemListState;
  rightAlignedComponent?: (isHighlighted: boolean) => ReactNode;
  chips?: DynamicContent;
  extraRow?: ExtraRow;
  isSpecial?: boolean;
  dropdown?: MDSMenuContentList;
  appearance: MDSRowItemAppearance;
};

export type ItemListCollectionItem = ItemListItem<ItemListItemKind.Collection>;
export type ItemListNoteItem = ItemListItem<ItemListItemKind.Note>;
